import { useCallback } from 'react';

import { type KnackCriteria } from '@/types/schema/KnackCriteria';
import { type KnackField } from '@/types/schema/KnackField';
import { type KnackObjectResponse } from '@/types/schema/KnackObject';
import { type ViewFilters } from '@/types/schema/LiveAppView';
import { isDateTimeRangeOperator, shouldHideValueBasedOnOperator } from '@/utils/field-operators';
import { type ViewWithQueryParams } from '@/components/views/ViewQueryParamsContext';
import { useCriteriaHelpers } from './useCriteriaHelpers';
import { useFieldHelpers } from './useFieldHelpers';

export function useViewFiltersHelpers() {
  const { canFieldStoreDateValues } = useFieldHelpers();
  const { getDefaultCriteriaOperator, getDefaultCriteriaValue } = useCriteriaHelpers();

  const getDefaultFilters = useCallback(
    (eligibleFilterCriteriaFields: KnackField[]) => {
      // If there are no eligible fields, return a filter with empty rules
      if (eligibleFilterCriteriaFields.length === 0) {
        const defaultFilters: ViewFilters = {
          match: 'and',
          rules: []
        };

        return defaultFilters;
      }

      const criteriaField = eligibleFilterCriteriaFields[0];
      const defaultFilters: ViewFilters = {
        match: 'and',
        rules: [
          {
            field: criteriaField.key,
            operator: getDefaultCriteriaOperator(criteriaField),
            value: getDefaultCriteriaValue(criteriaField)
          }
        ]
      };

      return defaultFilters;
    },
    [getDefaultCriteriaOperator, getDefaultCriteriaValue]
  );

  const getPresetFilters = useCallback((view: ViewWithQueryParams) => {
    if (view.type === 'search') {
      return undefined;
    }

    const allowsPresetFilters =
      view.type === 'map' ? view.details.allow_preset_filters : view.allow_preset_filters;
    const filterType = view.type === 'map' ? view.details.filter_type : view.filter_type;
    const shouldHavePresetFilters =
      allowsPresetFilters && filterType !== 'menu' && filterType !== 'none';

    if (!shouldHavePresetFilters) {
      return undefined;
    }

    const presetFilterCriteria =
      view.type === 'map' ? view.details.preset_filters : view.preset_filters;

    if (!presetFilterCriteria || presetFilterCriteria.length === 0) {
      return undefined;
    }

    const presetFilter: ViewFilters = {
      match: 'and',
      rules: presetFilterCriteria
    };

    return presetFilter;
  }, []);

  const getInitialMenuFilters = useCallback((view: ViewWithQueryParams) => {
    if (view.type === 'search') {
      return undefined;
    }

    const filterType = view.type === 'map' ? view.details.filter_type : view.filter_type;
    const shouldHaveMenuFilters = filterType === 'menu';

    if (!shouldHaveMenuFilters) {
      return undefined;
    }

    const menuFiltersRules = view.type === 'map' ? view.details.menu_filters : view.menu_filters;

    if (!menuFiltersRules || menuFiltersRules.length === 0) {
      return undefined;
    }

    // We only need the first menu filter rule, since that's the one that will be active by default
    const firstMenuFilterRule = menuFiltersRules[0];
    const menuFiltersFormatted: ViewFilters = {
      match: 'and',
      rules: [
        {
          field: firstMenuFilterRule.field,
          operator: firstMenuFilterRule.operator,
          value: firstMenuFilterRule.value
        }
      ]
    };

    return menuFiltersFormatted;
  }, []);

  const getNormalizedFiltersForSubmission = useCallback(
    (filters: ViewFilters, sourceObject: KnackObjectResponse): ViewFilters => {
      const rulesToAdd: KnackCriteria[] = [];

      filters.rules.forEach((rule) => {
        const criteriaField = [...sourceObject.fields, ...sourceObject.fieldsFromConnections].find(
          (field) => field.key === rule.field
        );

        if (!criteriaField) {
          return;
        }

        // Ignore rules that use the "is any" operator, since they don't affect the search
        if (rule.operator === 'is any') {
          return;
        }

        // Create a new rule object that only has the base properties for a filter rule.
        // This is to ensure that we don't include properties like `range` or `type` that are not applicable to all fields.
        const ruleToAdd: KnackCriteria = {
          field: rule.field,
          operator: rule.operator,
          value: rule.value
        };

        if (shouldHideValueBasedOnOperator(rule.operator)) {
          rulesToAdd.push({
            ...ruleToAdd,
            value: undefined
          });
          return;
        }

        if (criteriaField.type === 'connection' && Array.isArray(rule.value)) {
          rulesToAdd.push({
            ...ruleToAdd,
            value: [rule.value[0]]
          });
          return;
        }

        if (canFieldStoreDateValues(criteriaField) && isDateTimeRangeOperator(rule.operator)) {
          rulesToAdd.push({
            ...ruleToAdd,
            range: rule.range,
            type: rule.type,
            value: undefined
          });
          return;
        }

        if (criteriaField.type === 'address' && rule.operator === 'near') {
          rulesToAdd.push({
            ...ruleToAdd,
            range: rule.range
          });
          return;
        }

        rulesToAdd.push(ruleToAdd);
      });

      return {
        ...filters,
        rules: rulesToAdd
      };
    },
    [canFieldStoreDateValues]
  );

  return {
    getDefaultFilters,
    getPresetFilters,
    getInitialMenuFilters,
    getNormalizedFiltersForSubmission
  };
}
